// src/components/layouts/FloatingLayout.tsx
'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { getMenuOptions } from '@/components/menu';

export default function FloatingLayout({ children, title, userName, userRole, userAvatar, userInitials, handleLogout, config, API_URL }: any) {
  const pathname = usePathname();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // 💡 NUEVO: Control menú móvil
  const isActive = (path: string) => pathname === path;

  const menuConfig = getMenuOptions(userRole);
  const logoUrl = config?.logo_url ? (config.logo_url.startsWith('http') ? config.logo_url : `${API_URL}${config.logo_url}`) : null;
  const companyName = config?.company_name || "Cash Money";

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col font-sans">
      
      {/* NAVBAR SUPERIOR FLOTANTE */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            
            {/* Logo y Menú Principal */}
            <div className="flex items-center">
              {/* BOTÓN HAMBURGUESA MÓVIL */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 -ml-2 mr-2 text-gray-600 hover:bg-gray-100 rounded-xl">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} /></svg>
              </button>

              <div className="flex-shrink-0 flex items-center mr-8">
                 {logoUrl ? (
                    <img src={logoUrl} alt={companyName} className="h-8 object-contain" />
                 ) : (
                    <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm mr-2">
                      {companyName.charAt(0)}
                    </div>
                 )}
                 <span className="text-xl font-black text-gray-800 tracking-tight truncate hidden sm:block" title={companyName}>{companyName}</span>
              </div>
              
              {/* 🚀 RENDERIZADO DINÁMICO HORIZONTAL - SOLO DESKTOP */}
              <nav className="hidden lg:ml-6 lg:flex sm:space-x-2 xl:space-x-4 items-center">
                {menuConfig.map((group) => 
                  group.items.map((item: any, i: number) => (
                    <Link
                      key={`${group.section}-${i}`}
                      href={item.path}
                      className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                        isActive(item.path)
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span>{item.icon}</span>
                      <span className="hidden xl:inline">{item.name}</span>
                    </Link>
                  ))
                )}
              </nav>
            </div>

            {/* Perfil de Usuario */}
            <div className="flex items-center gap-4">
              <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-widest bg-gray-100 text-gray-600 px-2 py-1 rounded">
                {userRole}
              </span>
              <div className="relative">
                <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center text-sm bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm border border-gray-200 cursor-pointer">
                  {userAvatar ? (
                     <img src={userAvatar} alt={userName} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover" />
                  ) : (
                     <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-md sm:text-lg">
                       {userInitials}
                     </div>
                  )}
                </button>
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-2xl shadow-xl py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-100 mb-1">
                      <p className="text-sm font-bold text-gray-900 truncate">{userName}</p>
                      <p className="text-[10px] font-bold text-gray-400 uppercase sm:hidden">{userRole}</p>
                    </div>
                    <button onClick={handleLogout} className="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer font-medium">
                      🚪 Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* MENÚ MÓVIL DESPLEGABLE */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl absolute w-full left-0 z-40 max-h-[80vh] overflow-y-auto">
            <nav className="px-4 pt-2 pb-4 space-y-1">
              {menuConfig.map((group, index) => (
                <div key={index} className="pt-2">
                  <div className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{group.section}</div>
                  {group.items.map((item: any, i: number) => (
                    <Link
                      key={i}
                      href={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-blue-700 font-bold'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-lg">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* ÁREA DE CONTENIDO PRINCIPAL CENTRADA */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 lg:mt-4">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight truncate">{title}</h1>
        </div>
        
        <div className="bg-white lg:rounded-[1.5rem] rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 overflow-hidden min-h-[70vh]">
          {children}
        </div>
      </main>
    </div>
  );
}